import React from 'react';
import { Box, Group, ActionIcon, Text, Grid, Stack } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import classes from './mobileJournal.module.css';
import { IJournalEntry } from '../../../../global/entities/Journal/Journal';

dayjs.extend(utc);

interface MobileJournalCalendarProps {
  currentMonth: Date;
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  entries: IJournalEntry[];
  calendarDays: Date[];
  maxDots?: number;
}

const getStatusClass = (entry: IJournalEntry): string => {
  if (entry.isCompleted) return classes.statusDotCompleted;
  if (entry.isDraft) return classes.statusDotDraft;
  return classes.statusDotDefault;
};

const getEntriesForDate = (
  entries: IJournalEntry[],
  date: Date,
): IJournalEntry[] => {
  const targetDateString = dayjs(date).format('YYYY-MM-DD');
  return entries.filter((entry) => {
    const entryDateString = dayjs
      .utc(entry.entryDate)
      .local()
      .format('YYYY-MM-DD');
    return entryDateString === targetDateString;
  });
};

export const MobileJournalCalendar: React.FC<MobileJournalCalendarProps> = ({
  currentMonth,
  selectedDate,
  onDateSelect,
  onMonthChange,
  entries,
  calendarDays,
  maxDots = 3,
}) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const weekDaysFr = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  const localizedWeekDays = i18n.language === 'fr' ? weekDaysFr : weekDays;

  return (
    <Stack gap={0}>
      {/* Month Navigation */}
      <Group justify="center" gap="md" align="center" mb="xs">
        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={() =>
            onMonthChange(dayjs(currentMonth).subtract(1, 'month').toDate())
          }
        >
          <IconChevronLeft size={16} />
        </ActionIcon>

        <Text size="sm" fw={600} style={{ minWidth: 140, textAlign: 'center' }}>
          {currentMonth.toLocaleDateString(i18n.language, {
            month: 'long',
            year: 'numeric',
          })}
        </Text>

        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={() =>
            onMonthChange(dayjs(currentMonth).add(1, 'month').toDate())
          }
        >
          <IconChevronRight size={16} />
        </ActionIcon>
      </Group>

      {/* Calendar Grid */}
      <Box
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-sm)',
          overflow: 'hidden',
        }}
      >
        {/* Weekday Headers */}
        <Grid gutter={0}>
          {localizedWeekDays.map((day, index) => (
            <Grid.Col
              key={index}
              span={12 / 7}
              style={{ flexBasis: '14.28%', maxWidth: '14.28%' }}
            >
              <Box
                p={4}
                style={{
                  backgroundColor: 'var(--mantine-color-gray-1)',
                  borderRight:
                    index < 6
                      ? '1px solid var(--mantine-color-gray-3)'
                      : undefined,
                  borderBottom: '1px solid var(--mantine-color-gray-3)',
                  textAlign: 'center',
                }}
              >
                <Text size="xs" fw={600} c="dimmed">
                  {day}
                </Text>
              </Box>
            </Grid.Col>
          ))}
        </Grid>

        {/* Calendar Days */}
        <Grid gutter={0}>
          {calendarDays.map((date) => {
            const entriesForDate = getEntriesForDate(entries, date);
            const isCurrentMonth = dayjs(date).isSame(currentMonth, 'month');
            const isSelected = dayjs(date).isSame(selectedDate, 'day');
            const isToday = dayjs(date).isSame(dayjs(), 'day');

            const cellClasses = [
              classes.calendarCell,
              isSelected && classes.calendarCellSelected,
              isToday && !isSelected && classes.calendarCellToday,
              !isCurrentMonth && classes.calendarCellOutside,
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <Grid.Col
                key={date.toISOString()}
                span={12 / 7}
                style={{ flexBasis: '14.28%', maxWidth: '14.28%' }}
              >
                <Box className={cellClasses} onClick={() => onDateSelect(date)}>
                  <Text size="xs" fw={isToday ? 700 : 400}>
                    {dayjs(date).format('D')}
                  </Text>
                  {entriesForDate.length > 0 && (
                    <div className={classes.dotsRow}>
                      {entriesForDate.length <= maxDots ? (
                        entriesForDate.map((entry) => (
                          <span
                            key={entry.id}
                            className={`${classes.statusDot} ${getStatusClass(entry)}`}
                          />
                        ))
                      ) : (
                        <>
                          {entriesForDate.slice(0, maxDots - 1).map((entry) => (
                            <span
                              key={entry.id}
                              className={`${classes.statusDot} ${getStatusClass(entry)}`}
                            />
                          ))}
                          <Text size="8px" c="dimmed" lh={1}>
                            +{entriesForDate.length - (maxDots - 1)}
                          </Text>
                        </>
                      )}
                    </div>
                  )}
                </Box>
              </Grid.Col>
            );
          })}
        </Grid>
      </Box>
    </Stack>
  );
};
